/**
 * Cấu hình hiển thị theo trạng thái máy + badge trạng thái.
 * Màu dùng cho status indicator (không phải màu thương hiệu) nên set trực tiếp.
 */

import { AlertTriangle, Loader2, Power, WifiOff } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { MachineStatus } from '@/lib/tea-machines/types'
import { cn } from '@/lib/utils'
import { m } from '@/lib/paraglide/messages'

interface StatusStyle {
    icon: LucideIcon
    /** Màu chấm + badge. */
    dot: string
    badge: string
    /** Có nhấp nháy (chỉ máy đang chạy). */
    pulse: boolean
}

export const STATUS_STYLES: Record<MachineStatus, StatusStyle> = {
    running: {
        icon: Loader2,
        dot: 'bg-emerald-500',
        badge: 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-400',
        pulse: true,
    },
    stopped: {
        icon: Power,
        dot: 'bg-slate-400',
        badge: 'bg-muted text-muted-foreground',
        pulse: false,
    },
    offline: {
        icon: WifiOff,
        dot: 'bg-amber-500',
        badge: 'bg-amber-500/12 text-amber-700 dark:text-amber-400',
        pulse: false,
    },
    error: {
        icon: AlertTriangle,
        dot: 'bg-red-500',
        badge: 'bg-red-500/12 text-red-700 dark:text-red-400',
        pulse: false,
    },
}

/** Nhãn trạng thái (i18n) — gọi khi render, không chốt ở module scope. */
export function statusLabel(status: MachineStatus): string {
    switch (status) {
        case 'running':
            return m.tea_machines_status_running()
        case 'stopped':
            return m.tea_machines_status_stopped()
        case 'offline':
            return m.tea_machines_status_offline()
        case 'error':
            return m.tea_machines_status_error()
    }
}

export function StatusBadge({ status }: { status: MachineStatus }) {
    const s = STATUS_STYLES[status]
    const Icon = s.icon
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
                s.badge,
            )}
        >
            <Icon
                aria-hidden
                className={cn(
                    'size-3.5',
                    status === 'running' &&
                        'animate-spin motion-reduce:animate-none',
                )}
            />
            {statusLabel(status)}
        </span>
    )
}

/** Chấm trạng thái tròn — nhấp nháy khi máy đang chạy. */
export function StatusDot({ status }: { status: MachineStatus }) {
    const s = STATUS_STYLES[status]
    return (
        <span className="relative flex size-2.5 shrink-0">
            {s.pulse && (
                <span
                    className={cn(
                        'absolute inline-flex h-full w-full rounded-full opacity-75',
                        'animate-ping motion-reduce:animate-none',
                        s.dot,
                    )}
                />
            )}
            <span
                className={cn(
                    'relative inline-flex size-2.5 rounded-full',
                    s.dot,
                )}
            />
        </span>
    )
}
